/**
 * Official L'Eroica 2026 race-day route presets (provisional distances).
 * Source: https://www.eroica.cc/en/the-leroica-race/
 * Weekend: Saturday 3 October + Sunday 4 October 2026. ESM source; Jest uses
 * eroicaRoutes.cjs via sync:jtest-cjs.
 */

export const EROICA_SATURDAY_DATE = '2026-10-03';
export const EROICA_SUNDAY_DATE = '2026-10-04';
/** Weekend close (Sunday). Per-route `date` is the plan-generator source of truth. */
export const EROICA_EVENT_DATE = EROICA_SUNDAY_DATE;
export const EROICA_WEEKEND_LABEL = '3-4 October 2026';

export const EROICA_OFFICIAL_ROUTES_URL = 'https://www.eroica.cc/en/the-leroica-race/';

/** @typedef {'saturday' | 'sunday'} EroicaRaceDay */

/**
 * @typedef {{
 *   id: string,
 *   name: string,
 *   shortName: string,
 *   kind: string,
 *   aidStations: number,
 *   date: string,
 *   raceDay: EroicaRaceDay,
 *   distance: number,
 *   elevation: number,
 *   start: string,
 * }} EroicaRoute
 */

/** @type {readonly EroicaRoute[]} */
export const EROICA_ROUTES_2026 = Object.freeze([
  Object.freeze({
    id: 'leroico',
    name: "L'Eroica L'Eroico",
    shortName: "L'Eroico",
    kind: 'Long',
    aidStations: 6,
    date: EROICA_SATURDAY_DATE,
    raceDay: 'saturday',
    distance: 210.9,
    elevation: 3873,
    start: 'Gaiole in Chianti',
  }),
  Object.freeze({
    id: 'crete-senesi',
    name: "L'Eroica Crete Senesi",
    shortName: 'Crete Senesi',
    kind: 'Medium',
    aidStations: 4,
    date: EROICA_SATURDAY_DATE,
    raceDay: 'saturday',
    distance: 132.5,
    elevation: 2327,
    start: 'Gaiole in Chianti',
  }),
  Object.freeze({
    id: 'val-d-arbia',
    name: "L'Eroica Val d'Arbia",
    shortName: "Val d'Arbia",
    kind: 'Hundred',
    aidStations: 3,
    date: EROICA_SUNDAY_DATE,
    raceDay: 'sunday',
    distance: 106.3,
    elevation: 1795,
    start: 'Gaiole in Chianti',
  }),
  Object.freeze({
    id: 'gallo-nero',
    name: "L'Eroica Gallo Nero",
    shortName: 'Gallo Nero',
    kind: 'Short',
    aidStations: 2,
    date: EROICA_SUNDAY_DATE,
    raceDay: 'sunday',
    distance: 80.7,
    elevation: 1612,
    start: 'Gaiole in Chianti',
  }),
  Object.freeze({
    id: 'valle-del-chianti',
    name: "L'Eroica Valle del Chianti",
    shortName: 'Valle del Chianti',
    kind: 'Leisure',
    aidStations: 1,
    date: EROICA_SUNDAY_DATE,
    raceDay: 'sunday',
    distance: 45.1,
    elevation: 755,
    start: 'Gaiole in Chianti',
  }),
]);

/**
 * @param {EroicaRaceDay} raceDay
 * @returns {string}
 */
export function formatEroicaRaceDayLabel(raceDay) {
  switch (raceDay) {
    case 'saturday':
      return 'Saturday 3 October';
    case 'sunday':
      return 'Sunday 4 October';
    default: {
      const _exhaustive = raceDay;
      return String(_exhaustive);
    }
  }
}

/**
 * @param {readonly EroicaRoute[]} [routes]
 */
export function groupEroicaRoutesByRaceDay(routes = EROICA_ROUTES_2026) {
  const saturday = [];
  const sunday = [];
  for (let i = 0; i < routes.length; i += 1) {
    const route = routes[i];
    switch (route.raceDay) {
      case 'saturday':
        saturday.push(route);
        break;
      case 'sunday':
        sunday.push(route);
        break;
      default: {
        const _exhaustive = route.raceDay;
        void _exhaustive;
      }
    }
  }
  return Object.freeze([
    Object.freeze({
      raceDay: 'saturday',
      date: EROICA_SATURDAY_DATE,
      routes: Object.freeze(saturday),
    }),
    Object.freeze({
      raceDay: 'sunday',
      date: EROICA_SUNDAY_DATE,
      routes: Object.freeze(sunday),
    }),
  ]);
}

/** Plan-generator chip shape: name, date, distance, elevation */
export function eroicaRoutesAsPopularEvents() {
  return EROICA_ROUTES_2026.map((route) =>
    Object.freeze({
      name: route.name,
      date: route.date,
      distance: route.distance,
      elevation: route.elevation,
    })
  );
}
